/*
 * rmh3-delivery.js — the product page's ship promise and "Arrives Wed, Oct 14 – Thu,
 * Oct 15" for the shopper's ZIP (Dylan 2026-10-12: "build zip", then "build all":
 * the ship line follows the clock, the date follows the order's weight and the
 * ZIP's first three digits).
 *
 * Data: the shop metafield rmh3.delivery_estimates, written nightly by MHLHUB's
 * storefront_delivery_estimates job (server/services/shopify/deliveryEstimatePayload.js
 * documents the shape). The ZIP never leaves the browser.
 *
 *   ship day   = today if a weekday, not a ship holiday, and before the cutoff hour
 *                (Eastern); otherwise the next such day; + shipLag business days for
 *                an item that ships in 3 business days.
 *   arrives    = ship day + typical … + late business days, skipping weekends and
 *                the carrier holidays. [typical, late] comes from the most specific
 *                cell with enough history: ZIP3 × weight, state × weight, ZIP3, state.
 *   weight     = this item × quantity + the cart. Under heavy_lb rate shopping picks
 *                the Post Office; at or over it, mostly UPS/FedEx — about a day apart
 *                either way. A unit weighs our catalog's weight (variant metafield
 *                rmh3.ship_oz), else Shopify's; an item or cart line with neither
 *                makes the weight unknown, and the page uses the all-weights numbers.
 *
 * The pure part below is required by the MHLHUB test suite in Node; the DOM part
 * runs only in a browser.
 */
(function () {
  'use strict';

  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var GRAMS_PER_LB = 453.592;
  var EASTERN = null;

  /** { ymd: 'YYYY-MM-DD', hour } in America/New_York for an instant. */
  function eastern(now) {
    if (!EASTERN) {
      EASTERN = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23'
      });
    }
    var p = {};
    EASTERN.formatToParts(now).forEach(function (x) { p[x.type] = x.value; });
    return { ymd: p.year + '-' + p.month + '-' + p.day, hour: Number(p.hour) };
  }

  function toDate(ymd) { return new Date(ymd + 'T00:00:00Z'); }
  function toYmd(d) { return d.toISOString().slice(0, 10); }
  function addDays(ymd, n) { var d = toDate(ymd); d.setUTCDate(d.getUTCDate() + n); return toYmd(d); }
  function isWeekend(ymd) { var w = toDate(ymd).getUTCDay(); return w === 0 || w === 6; }
  function isOpen(ymd, closed) { return !isWeekend(ymd) && (closed || []).indexOf(ymd) === -1; }

  /** The next `n` open days after `ymd` (n = 0 returns ymd). */
  function addOpenDays(ymd, n, closed) {
    var d = ymd;
    for (var left = n; left > 0;) {
      d = addDays(d, 1);
      if (isOpen(d, closed)) left -= 1;
    }
    return d;
  }

  /** data may be null (no metafield yet): weekends and a 3 PM cutoff still apply. */
  function shipDay(now, data, shipLag) {
    data = data || {};
    var et = eastern(now);
    var d = et.ymd;
    var cutoff = typeof data.cutoff_hour === 'number' ? data.cutoff_hour : 15;
    if (!isOpen(d, data.ship_closed) || et.hour >= cutoff) {
      do { d = addDays(d, 1); } while (!isOpen(d, data.ship_closed));
    }
    return addOpenDays(d, shipLag || 0, data.ship_closed);
  }

  /** An in-stock item's ship day in words: 'today', 'tomorrow' or 'Mon, Oct 19'. */
  function shipWhen(now, data) {
    var day = shipDay(now, data, 0);
    var today = eastern(now).ymd;
    return { day: day, when: day === today ? 'today' : day === addDays(today, 1) ? 'tomorrow' : formatDay(day) };
  }

  function stateForZip(zip, ranges) {
    var p = String(zip).slice(0, 3);
    for (var i = 0; i < (ranges || []).length; i++) {
      if (p >= ranges[i][0] && p <= ranges[i][1]) return ranges[i][2];
    }
    return null;
  }

  /**
   * The order's weight in pounds, or null when any part of it is unknown.
   * itemGrams: one unit's weight (0 = none on file); cart: /cart?view=ship-weight
   * ({ total_weight, unknown }) or /cart.js ({ total_weight, items: [{ grams,
   * requires_shipping }] }) or null. Known weight at or over `heavyLb` is heavy
   * whatever else is unknown — more items only add weight.
   */
  function orderWeightLb(itemGrams, qty, cart, heavyLb) {
    var item = Number(itemGrams) > 0 ? Number(itemGrams) * Math.max(1, Number(qty) || 1) : 0;
    var cartGrams = cart ? Number(cart.total_weight) || 0 : 0;
    var lb = (item + cartGrams) / GRAMS_PER_LB;
    var cartUnknown = !!(cart && (typeof cart.unknown === 'boolean' ? cart.unknown
      : (cart.items || []).some(function (it) { return it.requires_shipping !== false && !(Number(it.grams) > 0); })));
    if (lb >= (heavyLb || 2)) return lb;
    if (!(Number(itemGrams) > 0) || cartUnknown) return null;
    return lb;
  }

  /** [typical, late] for this ZIP and weight, or null. Reads v2 and the v1 shape. */
  function pickDays(data, zip, state, weightLb) {
    var s = data.states && data.states[state];
    if (!s) return null;
    if (typeof s[0] === 'number') return [s[0], s[1]]; // v1: [typical, late, n]
    var z = (data.zip3s && data.zip3s[String(zip).slice(0, 3)]) || null;
    var band = weightLb == null ? 0 : weightLb < (data.heavy_lb || 2) ? 1 : 2;
    var order = band ? [z && z[band], s[band], z && z[0], s[0]] : [z && z[0], s[0]];
    for (var i = 0; i < order.length; i++) if (order[i]) return order[i];
    return null;
  }

  /**
   * { state, ship, from, to } — or { error: 'zip' } for a ZIP that is not 5 digits /
   * not a US prefix, { error: 'none', state } for a state we have no history for.
   * weightLb: orderWeightLb(), null when unknown.
   */
  function estimate(zip, data, now, shipLag, weightLb) {
    var z = String(zip || '').trim();
    if (!/^[0-9]{5}$/.test(z)) return { error: 'zip' };
    var state = stateForZip(z, data && data.zip3);
    if (!state) return { error: 'zip' };
    var days = pickDays(data, z, state, weightLb);
    if (!days) return { error: 'none', state: state };
    var ship = shipDay(now, data, shipLag);
    return {
      state: state,
      ship: ship,
      from: addOpenDays(ship, days[0], data.carrier_closed),
      to: addOpenDays(ship, days[1], data.carrier_closed)
    };
  }

  /** 'Wed, Oct 14' */
  function formatDay(ymd) {
    var d = toDate(ymd);
    return DOW[d.getUTCDay()] + ', ' + MON[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }

  var api = {
    estimate: estimate, shipDay: shipDay, shipWhen: shipWhen, orderWeightLb: orderWeightLb,
    addOpenDays: addOpenDays, stateForZip: stateForZip, formatDay: formatDay
  };
  if (typeof module === 'object' && module.exports) { module.exports = api; return; }

  // ── Browser ────────────────────────────────────────────────────────────────
  var STORE_KEY = 'rmh3:zip';
  function savedZip() { try { return window.localStorage.getItem(STORE_KEY) || ''; } catch (e) { return ''; } }
  function saveZip(z) { try { window.localStorage.setItem(STORE_KEY, z); } catch (e) {} }

  function readData() {
    var el = document.querySelector('[data-delivery-data]');
    try { return el ? JSON.parse(el.textContent || 'null') : null; } catch (e) { return null; }
  }

  // The server renders "Ships today if ordered by 3 PM ET" into a cached page; after
  // the cutoff, at a weekend or on a ship holiday that is no longer true.
  function renderShipLines(data) {
    var s = shipWhen(new Date(), data);
    document.querySelectorAll('[data-ship-line]').forEach(function (el) {
      if (!el.__rmh3Today) el.__rmh3Today = el.innerHTML;
      if (s.when === 'today') { el.innerHTML = el.__rmh3Today; return; }
      if (el.getAttribute('data-ship-line') === 'stock') { el.textContent = 'In stock · ships ' + s.when; return; }
      el.innerHTML = '';
      var strong = document.createElement('strong');
      strong.textContent = 'Ships ' + s.when;
      el.appendChild(strong);
    });
  }

  function bind(box, data) {
    if (box.__rmh3Delivery) return;
    box.__rmh3Delivery = true;
    if (!data || !data.states) { box.hidden = true; return; }
    var form = box.querySelector('[data-eta-form]');
    var input = box.querySelector('[data-eta-zip]');
    var out = box.querySelector('[data-eta-out]');
    var shipLag = Number(box.getAttribute('data-ship-lag') || 0);
    var itemGrams = Number(box.getAttribute('data-weight-g') || 0);
    var qtyEl = document.querySelector('[data-pdp-qty]');
    var cart = null;
    var shownZip = '';
    var strongEl = null;

    function weightLb() {
      return orderWeightLb(itemGrams, qtyEl ? qtyEl.value : 1, cart, data.heavy_lb);
    }
    function showForm(msg) {
      shownZip = '';
      strongEl = null;
      form.hidden = false;
      out.textContent = msg || 'Delivery date';
      out.classList.toggle('is-note', !!msg);
    }
    function render(zip, focusChange) {
      var r = estimate(zip, data, new Date(), shipLag, weightLb());
      if (r.error === 'zip') { showForm('Enter a 5-digit US ZIP'); return; }
      if (r.error === 'none') { showForm('No delivery estimate for ' + zip + ' yet'); return; }
      var range = r.from === r.to ? formatDay(r.from) : formatDay(r.from) + ' – ' + formatDay(r.to);
      // Same ZIP (a quantity change, the cart arriving, the minute tick): change the
      // words only. Rebuilding would swap out the Change button under the pointer —
      // the quantity box's change event fires on the mousedown that blurs it.
      if (zip === shownZip && strongEl) { strongEl.textContent = 'Arrives ' + range; return; }
      shownZip = zip;
      form.hidden = true;
      out.classList.remove('is-note');
      out.innerHTML = '';
      var strong = strongEl = document.createElement('strong');
      strong.textContent = 'Arrives ' + range;
      // "to 20151 · Change" wraps as one piece, so a narrow buy box never strands the dot.
      var to = document.createElement('span');
      to.className = 'pdp-eta__to';
      to.textContent = 'to ' + zip + ' · ';
      var change = document.createElement('button');
      change.type = 'button';
      change.className = 'pdp-eta__change';
      change.textContent = 'Change';
      change.addEventListener('click', function () { showForm(); input.value = zip; input.focus(); input.select(); });
      to.appendChild(change);
      out.appendChild(strong); out.appendChild(document.createTextNode(' ')); out.appendChild(to);
      if (focusChange) change.focus();
    }
    function refresh() { if (shownZip) render(shownZip, false); }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var z = String(input.value || '').replace(/[^0-9]/g, '').slice(0, 5);
      if (z.length === 5) saveZip(z);
      render(z, true);
    });
    if (qtyEl) { qtyEl.addEventListener('change', refresh); qtyEl.addEventListener('input', refresh); }
    var z0 = savedZip();
    if (z0) render(z0, false); else showForm();
    // What is already in the cart ships in the same box. The ship-weight view
    // (templates/cart.ship-weight.liquid) weighs it with our catalog's weights,
    // which /cart.js cannot see.
    if (window.fetch) {
      fetch('/cart?view=ship-weight', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c) { cart = c; refresh(); } })
        .catch(function () {});
    }
    return refresh;
  }

  var refreshers = [];
  function init() {
    var data = readData();
    renderShipLines(data);
    document.querySelectorAll('[data-delivery-estimate]').forEach(function (box) {
      var f = bind(box, data);
      if (f) refreshers.push(f);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  // A page left open past 3 PM (or midnight) moves its ship day without a reload.
  setInterval(function () {
    renderShipLines(readData());
    refreshers.forEach(function (f) { f(); });
  }, 60000);
})();
